import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks"

import { Launcher } from "./components/Launcher"
import { ehSaudacao, juntarHistorico, precisaDeConversaAoAbrir } from "./lib/abertura"
import { recuperarDoHistorico } from "./lib/recuperar"
import { devoTocar, prepararSom, tocarNotificacao } from "./lib/som"
import { Window } from "./components/Window"
import { applyBrandColors } from "./lib/brand"
import {
  createBridge,
  isStandalone,
  type ChatOrigin,
  type HostInbound,
  type VisitorIdentity,
} from "./lib/bridge"
import { ChatwebClient, temConversaGuardada } from "./lib/client"
import { isWithinBusinessHours } from "./lib/hours"
import type { Attendant, ChannelConfig, ChatMessage, Entry, Status } from "./lib/types"

const API_URL = import.meta.env.VITE_CHATBOT_API_URL || "http://localhost:8080"

let systemSeq = 0

/**
 * Espera entre dois avisos de leitura.
 *
 * Um pouco acima da trava do servidor (1s), para o aviso de arrasto chegar
 * DEPOIS da janela dele e nao ser descartado de novo.
 */
const INTERVALO_DE_LEITURA_MS = 1200

/**
 * Quando conferir o historico depois de mandar uma mensagem sem que nada tenha
 * chegado do outro lado. A primeira pega a resposta do fluxo, que sai em um ou
 * dois segundos; a segunda, um no mais lento (agente de IA). Ver
 * `recuperarPerdidas`.
 */
const CONFERIR_APOS_ENVIO_MS = [5000, 20000]

/**
 * Janela em que a conferencia de uma RECONEXAO e sorteada.
 *
 * No fim de um deploy todos os chats abertos reconectam juntos. Sem o sorteio,
 * cada um pediria o historico no mesmo segundo — em cima das proprias
 * reconexoes, que ja consultam o banco.
 */
const ESPALHAR_CONFERENCIA_DA_RECONEXAO_MS = 10000

export function App() {
  const standalone = useMemo(() => isStandalone(), [])

  const [open, setOpen] = useState(standalone)
  const [status, setStatus] = useState<Status>("idle")
  const [config, setConfig] = useState<ChannelConfig | null>(null)
  const [entries, setEntries] = useState<Entry[]>([])
  const [attendant, setAttendant] = useState<Attendant | null>(null)
  const [unread, setUnread] = useState(0)
  const [typing, setTyping] = useState(false)
  const [blockedReason, setBlockedReason] = useState<string | null>(null)
  // Comeca `true` (o seguro ate a sessao confirmar o contrario): grava e manda
  // audio e' so' para quem a conversa ja' amarrou a uma conta — anonimo manda
  // texto e anexo comum, igual sempre mandou.
  const [isGuest, setIsGuest] = useState(true)
  // Opaco: o servidor manda, a gente devolve. Ver `client.history`.
  const [cursor, setCursor] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  // Default alinhado com o do host (`frameBox` fechado): painel acima, a
  // direita, do tamanho de uma janela e sem destaque. Quem muda isso e o host,
  // pela mensagem `layout` — so ele enxerga a viewport e o link que abriu.
  const [layout, setLayout] = useState({
    above: true,
    toLeft: false,
    fullscreen: false,
    destaque: false,
    teclado: false,
    podeAmpliar: false,
  })

  const clientRef = useRef<ChatwebClient | null>(null)
  const bridgeRef = useRef<ReturnType<typeof createBridge> | null>(null)
  const endedRef = useRef(false)
  const pendingRefreshRef = useRef(false)
  /** Prazo da tentativa em curso. Cancelado quando a resposta chega. */
  const refreshTimerRef = useRef<number | null>(null)
  const queuedTextRef = useRef<string | null>(null)
  /** O botao que acompanhava o texto guardado — sem ele a retomada perde o id. */
  const queuedOptionRef = useRef<{ id: string; title: string } | null>(null)
  /** Token do modo avulso (`/v1/frame?token=`), para reconectar sem host. */
  const tokenAvulsoRef = useRef<string | null>(null)
  /** `connect` vive depois daqui; a referencia evita a dependencia circular. */
  const connectRef = useRef<((source: any, options?: any) => Promise<void>) | null>(null)
  /** Ultimo texto reenviado por conta propria — a retomada e uma so. */
  const ultimoReenvioRef = useRef<string | null>(null)
  const openRef = useRef(open)
  const typingTimer = useRef<number | null>(null)
  openRef.current = open
  const entriesRef = useRef(entries)
  entriesRef.current = entries
  /** Quando chegou a ultima mensagem do outro lado (fluxo ou atendente). */
  const ultimaSaidaRef = useRef(0)
  /** Conferencias de historico agendadas (envio ou reconexao). */
  const conferenciasRef = useRef(new Set<number>())
  /**
   * Um atendente entrou e ainda nao saiu. Nao da para ler isto do `attendant`:
   * o aviso de entrada pode vir sem nome, e ai ele fica `null`.
   */
  const emAtendimentoRef = useRef(false)

  const pushSystem = useCallback((text: string) => {
    setEntries((prev) => {
      // O mesmo aviso duas vezes seguidas nao informa mais do que uma: tres
      // envios que falham pelo mesmo motivo viravam tres linhas identicas.
      const ultima = prev[prev.length - 1]
      if (ultima?.kind === "system" && ultima.text === text) return prev
      return [
        ...prev,
        {
          kind: "system",
          id: `sys-${++systemSeq}`,
          text,
          timestamp: Math.floor(Date.now() / 1000),
        },
      ]
    })
  }, [])

  /**
   * Avisa o servidor que a pessoa LEU — e é o que faz o segundo tique aparecer
   * no Studio.
   *
   * A régua é a conversa estar na frente dela: janela aberta e aba visível. O
   * foco NÃO entra aqui (ao contrário do aviso sonoro): dá para ler a conversa
   * com o cursor na página do site, e exigir foco deixaria mensagem lida
   * marcada como não lida. Navegador fechado ou aba em segundo plano continua
   * com um tique só, que é o caso que o atendente precisa enxergar.
   */
  const ultimoAvisoDeLeituraRef = useRef(0)
  const avisoDeLeituraAgendadoRef = useRef<number | null>(null)

  const avisarQueLeu = useCallback(function avisar() {
    if (!openRef.current) return
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return

    // O servidor descarta avisos a menos de 1s um do outro. Numa rajada — o
    // fluxo manda tres mensagens seguidas —, o aviso da ULTIMA era o descartado,
    // e justo ela ficava com um tique so ate a pessoa fazer outra coisa. Em vez
    // de perder, agendamos UM aviso para o fim da rajada.
    const agora = Date.now()
    const desdeOUltimo = agora - ultimoAvisoDeLeituraRef.current
    if (desdeOUltimo < INTERVALO_DE_LEITURA_MS) {
      if (avisoDeLeituraAgendadoRef.current === null) {
        avisoDeLeituraAgendadoRef.current = window.setTimeout(() => {
          avisoDeLeituraAgendadoRef.current = null
          avisar()
        }, INTERVALO_DE_LEITURA_MS - desdeOUltimo)
      }
      return
    }

    ultimoAvisoDeLeituraRef.current = agora
    clientRef.current?.marcarLido()
  }, [])

  useEffect(
    () => () => {
      if (avisoDeLeituraAgendadoRef.current !== null) {
        window.clearTimeout(avisoDeLeituraAgendadoRef.current)
      }
    },
    [],
  )

  const pushMessage = useCallback((message: ChatMessage) => {
    setEntries((prev) => {
      if (prev.some((e) => e.kind === "message" && e.message.id === message.id)) {
        return prev
      }

      // O eco otimista e o eco do servidor sao a MESMA mensagem com ids
      // diferentes (`local-…` vs `chatweb-in-…`), entao a deduplicacao por id
      // nao pega — e a pessoa via o que escreveu duas vezes. Quando a versao do
      // servidor chega, ela substitui a local em vez de somar.
      if (message.direction === "inbound") {
        const localIndex = prev.findIndex(
          (e) =>
            e.kind === "message" &&
            e.message.id.startsWith("local-") &&
            e.message.direction === "inbound" &&
            e.message.text === message.text,
        )
        if (localIndex >= 0) {
          const next = prev.slice()
          next[localIndex] = { kind: "message", message }
          return next
        }
      }

      return [...prev, { kind: "message", message }]
    })

    if (message.direction === "outbound") {
      ultimaSaidaRef.current = Date.now()
      setTyping(false)
      if (!openRef.current) setUnread((n) => n + 1)

      // O aviso sonoro. `document.hasFocus()` aqui e do QUADRO do widget: com o
      // chat aberto mas a pessoa lendo a pagina do cliente, o foco esta la fora
      // e o som ainda faz sentido.
      if (
        devoTocar({
          direcao: message.direction,
          ehSaudacao: ehSaudacao(message),
          janelaAberta: openRef.current,
          documentoVisivel: document.visibilityState === "visible",
          quadroComFoco: document.hasFocus(),
        })
      ) {
        tocarNotificacao()
      }
      // Chegou com a conversa aberta na frente da pessoa: ja nasce lida.
      avisarQueLeu()
    }
  }, [avisarQueLeu])

  /**
   * Busca no historico o que o socket nao entregou.
   *
   * O socket e o caminho rapido, nao o unico. A resposta pode ter saido por uma
   * instancia do chatbot em que este socket nao esta — deploy sobrepondo a
   * revisao velha e a nova, as duas puxando do mesmo Pub/Sub —, ou no intervalo
   * em que o socket caiu e voltou. Ela esta gravada, e por isso "so aparecia
   * atualizando a pagina". Onde cada uma entra na tela e regra de
   * `recuperarDoHistorico`.
   */
  const recuperarPerdidas = useCallback(async () => {
    const client = clientRef.current
    if (!client || client.getSession()?.config?.features?.history === false) return

    const page = await client.history().catch(() => null)
    // Trocou de conversa enquanto o pedido ia: esse historico e de outra.
    if (!page?.messages.length || clientRef.current !== client) return

    const naTela = new Set(
      entriesRef.current.flatMap((e) => (e.kind === "message" ? [e.message.id] : [])),
    )
    const chegaramAgora = page.messages.filter(
      (m) => m.direction === "outbound" && !naTela.has(m.id) && !ehSaudacao(m),
    ).length

    setEntries((prev) => recuperarDoHistorico(prev, page.messages))
    if (!chegaramAgora) return
    ultimaSaidaRef.current = Date.now()
    setTyping(false)
    if (openRef.current) avisarQueLeu()
    else setUnread((n) => n + chegaramAgora)
  }, [avisarQueLeu])

  const agendarConferencia = useCallback(
    (espera: number, aindaPrecisa: () => boolean = () => true) => {
      const timer = window.setTimeout(() => {
        conferenciasRef.current.delete(timer)
        if (aindaPrecisa()) void recuperarPerdidas()
      }, espera)
      conferenciasRef.current.add(timer)
    },
    [recuperarPerdidas],
  )

  useEffect(() => {
    const conferencias = conferenciasRef.current
    return () => {
      conferencias.forEach((timer) => window.clearTimeout(timer))
      conferencias.clear()
    }
  }, [])

  /* ── conectar ──────────────────────────────────────────── */
  /**
   * Pedido pendente que NAO volta e o pior estado possivel.
   *
   * A trava existe para dois pedidos simultaneos nao virarem duas conversas.
   * So que ela era definitiva: se a resposta nunca chegasse — host fora do ar,
   * `embed.js` velho em cache, rede caida no meio — o widget ficava `idle` para
   * sempre, mostrando "offline" com o compositor habilitado, e todo clique
   * seguinte era engolido em silencio.
   *
   * Agora a trava tem prazo, e o pedido tem novas tentativas com espera
   * crescente. Depois da ultima, o estado vira `unavailable` — que ao menos
   * diz a verdade.
   */
  const ESPERAS = [4000, 8000, 15000]

  const requestSessionRefresh = useCallback(
    (
      /**
       * POR QUE estamos pedindo. Nao muda o pedido — a tela NUNCA e limpa aqui
       * (ver `clearEntries: false` na chegada da sessao) — mas diz, em cada
       * chamada, de qual dos tres casos ela veio.
       */
      motivo: "encerrada" | "credencial" | "inicio" = "encerrada",
      opcoes: { reabrirApenas?: boolean; tentativa?: number } = {},
    ) => {
      // Sem host nao ha a quem pedir sessao — mas no modo avulso o widget tem o
      // token publico e pode reconectar sozinho. Sem isto ele prometia "abrindo
      // uma nova conversa" e nao abria nada.
      if (standalone) {
        const token = tokenAvulsoRef.current
        if (token) {
          void connectRef.current?.(
            { publicToken: token },
            { replace: true, clearEntries: false },
          )
        }
        return
      }
      const tentativa = opcoes.tentativa ?? 0
      if (pendingRefreshRef.current && tentativa === 0) return

      pendingRefreshRef.current = true
      if (tentativa > 0) setStatus("connecting")

      bridgeRef.current?.post({
        type: "session-refresh",
        reabrirApenas: opcoes.reabrirApenas === true,
      })

      if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current)
      const espera = ESPERAS[Math.min(tentativa, ESPERAS.length - 1)]
      refreshTimerRef.current = window.setTimeout(() => {
        // Chegou sessao (ou `session-none`)? Entao nao ha o que tentar.
        if (!pendingRefreshRef.current) return
        pendingRefreshRef.current = false
        if (tentativa + 1 >= ESPERAS.length) {
          setStatus("unavailable")
          return
        }
        requestSessionRefresh(motivo, {
          reabrirApenas: opcoes.reabrirApenas,
          tentativa: tentativa + 1,
        })
      }, espera)
    },
    [standalone],
  )

  const sendText = useCallback(
    (text: string, option?: { id: string; title: string } | null) => {
      const client = clientRef.current

      // Sem cliente NAO e "nao faz nada".
      //
      // O cliente e descartado quando nao ha sessao (`session-none`). Daqui saia
      // um `return` mudo: a pessoa escrevia, mandava, e a tela nao dizia nada.
      // Agora o texto fica guardado e pedimos sessao — o proprio `connect` manda
      // o que ficou na fila assim que conectar.
      if (!client) {
        queuedTextRef.current = text
        queuedOptionRef.current = option ?? null
        requestSessionRefresh("credencial")
        pushSystem("Reconectando...")
        return
      }

      if (endedRef.current) {
        queuedTextRef.current = text
        queuedOptionRef.current = option ?? null
        requestSessionRefresh()
        pushSystem("Abrindo uma nova conversa...")
        return
      }

      const enviadaEm = Date.now()
      const comAtendente = emAtendimentoRef.current
      const pendingSend = client.send(text, null, option ?? null)

      const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
      pushMessage({
        id: localId,
        text,
        direction: "inbound",
        author: "customer",
        timestamp: Math.floor(Date.now() / 1000),
        attachments: [],
      })

      // NAO acender "digitando" aqui.
      //
      // Os tres pontinhos querem dizer UMA coisa: a outra pessoa esta
      // escrevendo agora. Acende-los porque NOS mandamos uma mensagem e uma
      // mentira — o atendente podia nem ter aberto a conversa. Quem acende e o
      // evento `typing` do servidor.

      void pendingSend.then((result) => {
        if (result.ok) {
          // A resposta deveria vir pelo socket. Se nada chegou do outro lado
          // desde o envio, confere o historico — ver `recuperarPerdidas`.
          //
          // Com atendente, nao: quem responde e uma pessoa, que costuma levar
          // mais que as duas conferencias — elas quase nunca achariam nada e
          // seriam o unico custo repetido a cada mensagem. A resposta dele que
          // se perder volta na reconexao. Vale o estado de QUANDO a pessoa
          // mandou: se o fluxo transferir logo depois, as mensagens dele antes
          // do aviso sao justamente o caso que a conferencia recupera.
          if (comAtendente) return
          for (const espera of CONFERIR_APOS_ENVIO_MS) {
            agendarConferencia(espera, () => ultimaSaidaRef.current < enviadaEm)
          }
          return
        }
        setEntries((prev) =>
          prev.filter((entry) => entry.kind !== "message" || entry.message.id !== localId),
        )
        setTyping(false)
        // A retomada e UMA por mensagem, qualquer que seja o motivo.
        //
        // Sem este teto, cada falha abria outra conversa e tentava de novo: uma
        // mensagem so chegou a criar tres conversas seguidas, e no fim a tela
        // ainda dizia que nao tinha conseguido enviar. Uma tentativa e ajuda;
        // uma cadeia e estrago.
        const podeRetomar = ultimoReenvioRef.current !== text

        // Encerrada e "sumiu" dao no mesmo do lado de quem escreve: a conversa
        // nao existe mais. Mandar "tente de novo" ali e pedir para insistir
        // contra algo que nao volta — o util e abrir outra e mandar o texto.
        if (
          (result.error === "conversation_closed" ||
            result.error === "conversation_not_found") &&
          podeRetomar
        ) {
          ultimoReenvioRef.current = text
          endedRef.current = true
          queuedTextRef.current = text
          queuedOptionRef.current = option ?? null
          requestSessionRefresh()
          pushSystem("Conversa anterior encerrada. Abrindo uma nova conversa...")
          return
        }
        // Conexao caida: guarda o texto e refaz a sessao, em vez de mandar a
        // pessoa "tentar de novo" — ela tentaria contra o mesmo socket morto.
        //
        // Vale para os dois casos: `sem_conexao` (o socket sabe que caiu) e
        // `timeout` (ele ainda se acha conectado e ninguem respondeu) — o
        // segundo e o da aba que ficou parada em segundo plano.
        //
        if (
          (result.error === "sem_conexao" || result.error === "timeout") &&
          podeRetomar
        ) {
          ultimoReenvioRef.current = text
          queuedTextRef.current = text
          requestSessionRefresh("credencial")
          pushSystem("Reconectando...")
          return
        }

        // Qualquer outro motivo — inclusive um que ainda nao exista — nao pode
        // sumir. Era o que acontecia: a bolha ia embora e a tela nao dizia
        // nada, e a pessoa so via a mensagem desaparecer.
        pushSystem("Não consegui enviar sua mensagem. Tente de novo.")
      })
    },
    [agendarConferencia, pushMessage, pushSystem, requestSessionRefresh],
  )

  const connect = useCallback(
    async (
      source:
        | {
            publicToken: string
            visitorIdentity?: VisitorIdentity | null
            reabrirApenas?: boolean
            origin?: ChatOrigin | null
            interestCode?: string | null
          }
        | { session: any },
      options: { replace?: boolean; clearEntries?: boolean } = {},
    ) => {
      if (clientRef.current && !options.replace) return

      if (options.replace) {
        clientRef.current?.destroy()
        clientRef.current = null
        setAttendant(null)
        emAtendimentoRef.current = false
        setTyping(false)
        setBlockedReason(null)
        if (options.clearEntries) {
          setEntries([])
          setCursor(null)
        }
      }

      const client = new ChatwebClient(API_URL, {
        onStatus: (proximo) => {
          setStatus(proximo)
          // A fila espera o socket, nao o relogio.
          //
          // O texto guardado era disparado por um `setTimeout(0)` logo depois de
          // a sessao chegar — e o socket novo ainda nao tinha conectado, entao a
          // mensagem falhava com `sem_conexao` e a pessoa via "nao consegui
          // enviar" mesmo com a conversa nova ja aberta na frente dela.
          if (proximo !== "connected") return
          const guardado = queuedTextRef.current
          if (!guardado) return
          const opcaoGuardada = queuedOptionRef.current
          queuedTextRef.current = null
          queuedOptionRef.current = null
          window.setTimeout(() => sendText(guardado, opcaoGuardada), 0)
        },
        onMessage: pushMessage,
        onRead: (readAt) => {
          // O atendente leu. Vale para TUDO o que a pessoa mandou e ainda estava
          // sem visto: o aviso do outro lado e da conversa, nao de uma mensagem.
          setEntries((prev) =>
            prev.map((entrada) =>
              entrada.kind === "message" &&
              entrada.message.direction === "inbound" &&
              !entrada.message.readAt
                ? { ...entrada, message: { ...entrada.message, readAt } }
                : entrada,
            ),
          )
        },
        onTyping: () => {
          setTyping(true)
          if (typingTimer.current) window.clearTimeout(typingTimer.current)
          // O servidor manda um evento por vez, sem "parou de digitar": o
          // indicador apaga sozinho se nao vier outro.
          typingTimer.current = window.setTimeout(() => setTyping(false), 6000)
        },
        // Voltou depois de cair (ou de trocar a credencial): o que saiu nesse
        // intervalo nao vem de novo pelo socket. Sorteado para o fim de um
        // deploy nao virar uma rajada — ver ESPALHAR_CONFERENCIA_DA_RECONEXAO_MS.
        onReconectado: () =>
          agendarConferencia(Math.random() * ESPALHAR_CONFERENCIA_DA_RECONEXAO_MS),
        onHandoff: (next) => {
          emAtendimentoRef.current = true
          setAttendant(next)
          pushSystem(
            next?.name
              ? `${next.name} entrou na conversa.`
              : "Um atendente entrou na conversa.",
          )
        },
        onClosed: (fluxoSegue) => {
          // O ATENDENTE saiu, mas a conversa segue.
          //
          // Quando o fluxo tem proximo no, ele continua vivo e e ele que
          // pergunta "Deseja realizar novas operacoes?" com os botoes. Marcar
          // fim aqui fazia a resposta abrir uma conversa NOVA: o "Nao" nunca
          // chegava ao no que o esperava, e a pessoa recebia a saudacao de
          // volta — reiniciando justamente o que ela acabara de recusar.
          //
          // Entao: tira o cracha do atendente (quem fala agora e o robo) e sai
          // sem escrever nada. O aviso quem da e o proprio fluxo, na mensagem
          // que ele acabou de mandar.
          emAtendimentoRef.current = false
          if (fluxoSegue) {
            setAttendant(null)
            setTyping(false)
            return
          }

          // Encerrar NAO recomeca.
          //
          // O widget pedia sessao nova aqui, na hora, e a saudacao aparecia
          // sozinha logo abaixo do "Conversa encerrada." — o chat voltava do
          // zero sem ninguem ter pedido, e a despedida do atendente ficava
          // enterrada por um "Oi! Como podemos te ajudar?".
          //
          // A conversa nova nasce quando a PESSOA age: abrindo o painel de novo
          // (efeito do `open`) ou escrevendo (`sendText`, que enfileira o texto
          // e o manda assim que a sessao chega).
          //
          // Avisado duas vezes NAO vira duas linhas. O servidor reavisa `closed`
          // a CADA conexao numa conversa finalizada (`emitClosedIfInactive`), e
          // o socket reconecta sozinho — sem esta guarda, quem deixasse a aba
          // aberta juntava "Conversa encerrada." uma vez por reconexao.
          if (endedRef.current) return
          endedRef.current = true
          setTyping(false)
          bridgeRef.current?.post({ type: "closed" })
          pushSystem("Conversa encerrada.")
        },
        // A credencial venceu e quem a resolve e o host. REABRIR APENAS: o
        // relogio nao e a pessoa. Sem a bandeira, o vencimento do token pedia
        // sessao como se fosse um clique na bolha, e o servidor reativava a
        // conversa que o atendente tinha acabado de finalizar — o card voltava
        // para a lista "ATIVAS" sem ninguem ter mexido no chat.
        onSessionExpiring: () =>
          requestSessionRefresh("credencial", { reabrirApenas: true }),
        onError: (code) => {
          if (
            code === "guest_limit_reached" &&
            clientRef.current?.getSession()?.config?.features?.requiresIdentification !== true
          ) {
            pushSystem("Nao consegui enviar agora. Aguarde um instante e tente novamente.")
            return
          }
          if (code === "rate_limited") {
            pushSystem("Você enviou muitas mensagens seguidas. Aguarde um instante.")
          } else if (code === "guest_limit_reached") {
            setBlockedReason("Faça login para continuar")
            pushSystem("Para continuar o atendimento, entre na sua conta.")
          } else if (code === "unauthorized") {
            // O cliente ja tentou renovar sozinho e nao conseguiu (sessao
            // resolvida pelo host). A conversa continua viva la — quem encerra
            // e o atendente ou a palavra de saida, nao o relogio —, entao
            // pedimos credencial nova em vez de declarar indisponivel.
            //
            // REABRIR APENAS pelo mesmo motivo do `onSessionExpiring`: um token
            // recusado e o socket falando, nao a pessoa. Se a conversa acabou de
            // ser finalizada, o que a gente quer aqui e reanexar e ouvir o
            // `closed` — nunca ressuscitar o card na fila do atendente.
            setStatus("reconnecting")
            requestSessionRefresh("credencial", { reabrirApenas: true })
          }
        },
      })
      clientRef.current = client

      try {
        const session =
          "session" in source
            ? client.resume(source.session)
            : await client.connect(source.publicToken, {
                visitorIdentity: source.visitorIdentity ?? null,
                reabrirApenas: source.reabrirApenas === true,
                origin: source.origin ?? null,
                interestCode: source.interestCode ?? null,
              })

        // Nao havia conversa para reabrir, e nao era para criar: fica so a
        // bolha. O cliente e descartado para que o clique peca sessao de novo,
        // agora podendo criar.
        if (!session) {
          client.destroy()
          clientRef.current = null
          pendingRefreshRef.current = false
          return
        }

        setConfig(session.config)
        setIsGuest(session.identity?.isGuest !== false)
        setBlockedReason(null)
        pendingRefreshRef.current = false
        endedRef.current = false
        // Antes do primeiro paint da janela: aplicada depois, a pessoa veria o
        // verde padrao piscar e virar a cor da loja.
        applyBrandColors(document.documentElement, session.config?.brand ?? {})

        if (session.config?.features?.history !== false) {
          const page = await client.history()
          if (page.messages.length) {
            const novas = page.messages.map((message) => ({ kind: "message" as const, message }))
            // Onde o historico entra depende do que ja esta na tela — inclusive
            // do aviso que o socket pode ter trazido enquanto ele carregava.
            // A regra inteira, com os tres casos, mora em `juntarHistorico`.
            setEntries((anteriores) => juntarHistorico(anteriores, novas))
            setCursor(page.nextCursor)
          } else if (session.config?.greeting) {
            // Rede de segurança. Desde 2026-08-29 o servidor GRAVA a saudação
            // quando a conversa nasce, então o histórico já vem com ela e este
            // ramo não entra. Ele cobre a conversa antiga cujo histórico está
            // vazio — e, gravada ou não, a saudação nunca aparece no fim de uma
            // conversa que já tem mensagem.
            pushMessage({
              id: "greeting",
              text: session.config.greeting,
              direction: "outbound",
              author: "assistant",
              timestamp: Math.floor(Date.now() / 1000),
              attachments: [],
            })
          }
        }
      } catch (erro: any) {
        pendingRefreshRef.current = false
        setStatus("unavailable")
        // Token publico recusado (expirado, revogado, ambiente trocado): o host
        // guarda esse token entre um pedido e outro e nao tem como descobrir
        // isso sozinho — so quem faz a troca em `/chatweb/session` ve o 401.
        // Sem o aviso, a aba insistiria no mesmo token morto.
        if (/^session_failed_(401|403)$/.test(String(erro?.message || ""))) {
          bridgeRef.current?.post({ type: "session-rejected" })
        }
      }
    },
    [agendarConferencia, pushMessage, pushSystem, requestSessionRefresh, sendText],
  )

  /* ── ponte com o host ──────────────────────────────────── */
  useEffect(() => {
    const onHostMessage = (message: HostInbound) => {
      switch (message.type) {
        case "session":
          if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current)
          refreshTimerRef.current = null
          // Mensagem inicial do host (venda assistida): o checkout ja escreveu o
          // interesse com o codigo, e a pessoa nao deveria ter de digitar "oi"
          // para a conversa comecar — o atendente recebia um "oi" no lugar do
          // carrinho. Entra na fila e sai quando o socket novo conectar, o mesmo
          // caminho do texto guardado numa reconexao.
          if (message.initialMessage) {
            queuedTextRef.current = message.initialMessage
            queuedOptionRef.current = null
          }
          void connect(
            "session" in message
              ? { session: message.session }
              : {
                  publicToken: message.publicToken,
                  visitorIdentity: message.visitorIdentity ?? null,
                  reabrirApenas: message.reabrirApenas === true,
                  origin: message.origin ?? null,
                  interestCode: message.interestCode ?? null,
                },
            {
              replace: clientRef.current !== null,
              // A conversa encerrada continua NA TELA.
              //
              // Apagar tudo quando o atendente finaliza tirava da frente da
              // pessoa o que ela tinha acabado de combinar. A conversa nova
              // comeca abaixo da linha "Conversa encerrada." — o que ela mandar
              // dali em diante entra num fluxo e numa sessao novos, e isso e do
              // servidor; a tela so nao esquece o que ja aconteceu.
              clearEntries: false,
            },
          )
          break
        case "session-none":
          // Nao havia o que reabrir. Resposta legitima, nao falha: solta a
          // trava (e o prazo) para que o clique possa pedir de novo, agora
          // podendo criar.
          if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current)
          refreshTimerRef.current = null
          pendingRefreshRef.current = false

          // MAS o silencio so vale com o painel fechado.
          //
          // Na entrada, "nao ha conversa anterior" e a resposta certa e a tela
          // segue sendo a bolha. Com o painel ABERTO e sem cliente nenhum, a
          // mesma mensagem quer dizer outra coisa — nao foi possivel abrir
          // conversa —, e a pessoa esta olhando. Sem este aviso ela ficava com
          // um cabecalho "offline" e um campo que nao leva a lugar nenhum; foi
          // relatado exatamente assim: "clico na caixa de escrever e trava".
          if (openRef.current && !clientRef.current) setStatus("unavailable")
          break
        case "identity-reset": {
          // A sessao morre AQUI, antes de qualquer ida ao servidor.
          //
          // O token da conversa so existe na memoria deste iframe, entao
          // descartar o cliente ja o apaga — e e por isso que este ramo nao
          // espera resposta de nada. Quem sentar no computador em seguida nao
          // tem mais como usar a conversa da pessoa anterior.
          clientRef.current?.destroy()
          clientRef.current = null
          setEntries([])
          setCursor(null)
          setAttendant(null)
          emAtendimentoRef.current = false
          setTyping(false)
          setUnread(0)
          setBlockedReason(null)
          setOpen(false)
          endedRef.current = false
          queuedTextRef.current = null
          queuedOptionRef.current = null

          // O `visitorToken` guardado NAO e apagado de proposito: e ele que faz
          // o servidor reconhecer o navegador voltando sem a identidade — e e
          // nesse reconhecimento que ele derruba os tokens que ainda estiverem
          // em pe para aquela conversa. Apagar aqui esconderia dele o que
          // precisa ser revogado. A resposta vem 204, e fica so a bolha.
          requestSessionRefresh("credencial", { reabrirApenas: true })
          break
        }
        case "open":
          setOpen(true)
          break
        case "close":
          setOpen(false)
          break
        case "theme":
          document.documentElement.setAttribute("data-theme", message.theme)
          break
        case "layout":
          setLayout({
            above: message.above,
            toLeft: message.toLeft,
            fullscreen: message.fullscreen,
            destaque: message.destaque,
            teclado: message.teclado,
            podeAmpliar: message.podeAmpliar,
          })
          break
      }
    }

    const bridge = createBridge(onHostMessage)
    bridgeRef.current = bridge
    bridge.post({ type: "ready" })

    // Quem JA tem conversa guardada volta conectado, sem esperar clique:
    // reabrir nao cria nada, e e o que traz historico e nao-lidas de volta. Sem
    // conversa anterior, nada acontece ate a pessoa abrir o painel.
    if (!isStandalone() && temConversaGuardada()) {
      requestSessionRefresh("inicio", { reabrirApenas: true })
    }

    // Aberto direto em /v1/frame, sem host: o token vem da query, para dar para
    // exercitar o widget contra um backend real sem montar uma página inteira.
    if (isStandalone()) {
      const token = new URLSearchParams(window.location.search).get("token")
      tokenAvulsoRef.current = token
      if (token) void connect({ publicToken: token })
      else setStatus("unavailable")
    }

    return () => {
      bridge.destroy()
      clientRef.current?.destroy()
    }
  }, [connect])

  useEffect(() => {
    connectRef.current = connect
  }, [connect])

  /* ── avisar o host ─────────────────────────────────────── */
  // Voltar para a aba com a conversa aberta conta como ler: e o caso de quem
  // deixou o chat aberto, foi fazer outra coisa e voltou.
  useEffect(() => {
    const aoTrocarVisibilidade = () => avisarQueLeu()
    document.addEventListener("visibilitychange", aoTrocarVisibilidade)
    return () => document.removeEventListener("visibilitychange", aoTrocarVisibilidade)
  }, [avisarQueLeu])

  useEffect(() => {
    bridgeRef.current?.post({ type: "resize", open })
    if (open) {
      setUnread(0)
      avisarQueLeu()
    }

    // A conversa nasce no CLIQUE, nao no carregamento da pagina.
    //
    // Abrir sessao ao montar criava uma conversa a cada visita — e cada refresh
    // do site virava um card novo na fila do atendente, de gente que nao pediu
    // atendimento nenhum. O host so resolve a sessao na entrada quando ja existe
    // conversa anterior (reabrir nao cria nada); quando nao existe, o widget
    // fica so com a bolha e pede a sessao aqui, quando a pessoa abre o painel.
    //
    // A conversa ENCERRADA nao entra nesta conta: abrir o painel para reler a
    // despedida devolvia o card para a lista "ATIVAS" do atendente. Quem
    // recomeca e quem escreve — o `sendText` cuida disso. A regra inteira, com
    // o porque, mora em `precisaDeConversaAoAbrir`.
    const precisa = precisaDeConversaAoAbrir({
      open,
      temCliente: clientRef.current !== null,
    })
    if (precisa) requestSessionRefresh("inicio")
  }, [open, requestSessionRefresh])

  useEffect(() => {
    bridgeRef.current?.post({ type: "unread", count: unread })
  }, [unread])

  /* ── enviar ────────────────────────────────────────────── */
  /**
   * Sobe o arquivo e manda a mensagem.
   *
   * O eco local fica por conta do servidor aqui, e nao do widget: o anexo so
   * existe DEPOIS do upload, e inventar um eco antes disso mostraria na tela um
   * arquivo que ainda pode falhar.
   */
  const onAttach = useCallback(async (file: File) => {
    const client = clientRef.current
    if (!client) throw new Error("sem_conexao")

    const anexo = await client.upload(file)
    const result = await client.send("", anexo)
    if (!result.ok) {
      throw new Error(result.error)
    }
  }, [])

  const onLoadMore = useCallback(async () => {
    const client = clientRef.current
    if (!client || cursor === null) return
    setLoadingMore(true)
    try {
      const page = await client.history(cursor)
      setEntries((prev) => [
        ...page.messages.map((message) => ({ kind: "message" as const, message })),
        ...prev,
      ])
      setCursor(page.nextCursor)
    } finally {
      setLoadingMore(false)
    }
  }, [cursor])

  const onDrag = useCallback(
    (phase: "start" | "move" | "end", dx: number, dy: number) => {
      bridgeRef.current?.post({ type: "drag", phase, dx, dy })
    },
    [],
  )

  const offline = !isWithinBusinessHours(config)

  // Escrever nao depende de estar CONECTADO — depende de haver conversa.
  //
  // Socket caido continua aceitando texto: a conversa existe, o que a pessoa
  // escrever e enfileirado e sai quando ele volta. Preso a `status` inteiro, um
  // tropeco de rede desabilitava o campo e o envio virava um `return` mudo.
  //
  // Antes da PRIMEIRA sessao e outra coisa: nao ha para onde mandar. Na pagina
  // de um cliente e ali que mora o reCAPTCHA — e quando o Google resolve
  // mostrar a grade de imagens, isso leva o tempo que a pessoa levar. Nesses
  // segundos o texto ia para uma fila que podia nunca sair, e o que ela via era
  // "Nao consegui enviar sua mensagem. Tente de novo." Relatado em producao,
  // com o desafio aberto na tela e o campo aceitando texto.
  const semConversaAinda =
    status === "idle" || status === "connecting" || status === "unavailable"
  const canSend = blockedReason === null && !semConversaAinda

  // Tela cheia e destaque sao a MESMA coisa para a janela: ela virou modal —
  // ganha `aria-modal`, e o Esc fecha. No celular porque nao ha pagina visivel
  // atras; no desktop porque a pagina esta escurecida pela cortina do host.
  const modal = layout.fullscreen || layout.destaque

  return (
    <div
      class={`app${standalone ? " standalone" : ""}${layout.toLeft ? " left" : ""}${
        layout.above ? "" : " below"
      }${layout.fullscreen ? " fullscreen" : ""}${layout.destaque ? " destaque" : ""}${
        layout.teclado ? " teclado" : ""
      }`}
    >
      {open && (
        <Window
          config={config}
          status={status}
          entries={entries}
          typing={typing}
          attendant={attendant}
          offline={offline}
          canSend={canSend}
          isGuest={isGuest}
          blockedReason={blockedReason}
          hasMore={cursor !== null}
          loadingMore={loadingMore}
          onLoadMore={onLoadMore}
          onSend={sendText}
          onAttach={config?.features?.attachments === false ? null : onAttach}
          modal={modal}
          telaCheia={layout.fullscreen}
          onAlternarTelaCheia={
            layout.podeAmpliar
              ? () => bridgeRef.current?.post({ type: "fullscreen", on: !layout.fullscreen })
              : null
          }
          onClose={() => setOpen(false)}
        />
      )}

      {/* Em tela cheia a bolha sai: nao ha canto onde ela nao cubra a conversa,
          e quem fecha e o X do cabecalho — que e onde a pessoa procura. */}
      {!standalone && !layout.fullscreen && (
        <Launcher
          open={open}
          unread={unread}
          draggable={config?.features?.draggable !== false}
          onToggle={() => {
            // O navegador so libera audio depois de um gesto NESTE quadro; este
            // clique e o primeiro que existe.
            prepararSom()
            setOpen((value) => !value)
          }}
          onDrag={onDrag}
        />
      )}
    </div>
  )
}
